import React, { useState } from 'react';
import { X, Smartphone, AlertTriangle } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface MFALoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerify: (code: string) => Promise<boolean>;
  loading?: boolean;
  userEmail?: string;
}

export default function MFALoginModal({
  isOpen,
  onClose,
  onVerify,
  loading = false,
  userEmail
}: MFALoginModalProps) {
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [attempts, setAttempts] = useState(0);

  if (!isOpen) return null;

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit verification code');
      return;
    }

    setIsVerifying(true);
    try {
      const success = await onVerify(verificationCode);
      if (success) {
        toast.success('Login successful!');
        // Modal will be closed by parent component
      } else {
        setAttempts(prev => prev + 1);
        toast.error('Invalid verification code. Please try again.');
        setVerificationCode('');
        
        if (attempts >= 2) {
          toast.error('Too many failed attempts. Please try again later.');
          onClose();
        }
      }
    } catch (error) {
      toast.error('Verification failed. Please try again.');
      setVerificationCode('');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && verificationCode.length === 6) {
      handleVerify();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Two-Factor Authentication
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            disabled={loading || isVerifying}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Smartphone className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Enter Verification Code
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Open your authenticator app and enter the 6-digit code for MoZo.Live
            </p>
            {userEmail && (
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                Signing in as: {userEmail}
              </p>
            )}
          </div>

          <div className="mb-6">
            <label htmlFor="login-verification-code" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Verification Code
            </label>
            <input
              id="login-verification-code"
              type="text"
              value={verificationCode}
              onChange={handleCodeChange}
              onKeyPress={handleKeyPress}
              placeholder="000000"
              className="w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-center text-xl font-mono tracking-widest"
              maxLength={6}
              disabled={isVerifying}
              autoFocus
            />
          </div>

          {attempts > 0 && (
            <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
              <div className="flex items-center">
                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mr-2" />
                <span className="text-sm text-amber-700 dark:text-amber-300">
                  {attempts === 1 ? 'Invalid code. Please try again.' : `${attempts} failed attempts. ${3 - attempts} attempts remaining.`}
                </span>
              </div>
            </div>
          )}

          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-3 px-4 rounded-md transition-colors"
              disabled={isVerifying}
            >
              Cancel
            </button>
            <button
              onClick={handleVerify}
              disabled={verificationCode.length !== 6 || isVerifying}
              className="flex-1 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-md transition-colors"
            >
              {isVerifying ? 'Verifying...' : 'Verify & Sign In'}
            </button>
          </div>

          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Don't have access to your authenticator app?{' '}
              <button 
                onClick={onClose}
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                Contact Support
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
